
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { BrainCircuit, ArrowUp, Sparkles, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
}

const AISessionAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: "Hello! I'm your AI session assistant. I can help with technical questions or provide suggestions during your collaboration session.",
      sender: 'assistant',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current;
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsProcessing(true);
    
    // Simulate AI response
    setTimeout(() => {
      const assistantResponse = getAIResponse(inputValue);
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: assistantResponse,
        sender: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      setIsProcessing(false);
    }, 1500);
  };
  
  // Simulate AI responses for demo purposes
  const getAIResponse = (query: string): string => {
    const normalizedQuery = query.toLowerCase();
    
    if (normalizedQuery.includes('latency') || normalizedQuery.includes('delay')) {
      return "To reduce latency, try these steps: 1) Close unnecessary applications, 2) Use a wired ethernet connection instead of WiFi, 3) Lower the buffer size in your audio settings, 4) Make sure all participants have updated to the latest client version.";
    }
    
    if (normalizedQuery.includes('sync') || normalizedQuery.includes('timing')) {
      return "If you're having sync issues between tracks, try using the 'Session Sync' tool in the mixer tab. This will analyze timing differences between audio tracks and apply subtle adjustments to align them.";
    }
    
    if (normalizedQuery.includes('recording') || normalizedQuery.includes('record')) {
      return "To record this session, click the red circle button in the controls bar at the bottom. All participants' audio will be recorded to separate tracks, which you can download afterward.";
    }
    
    if (normalizedQuery.includes('export') || normalizedQuery.includes('download')) {
      return "You can export session recordings from the Files tab in the sidebar. Individual tracks are available in WAV format, and a quick-mix MP3 is also generated automatically.";
    }
    
    return "I don't have specific information about that. Could you ask in a different way or try another question about your session or audio settings?";
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BrainCircuit className="h-4 w-4 text-purple-400" />
          <h3 className="text-sm font-medium text-white">AI Session Assistant</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-white/70 hover:text-white hover:bg-white/10 text-xs"
          onClick={() => {
            toast({
              title: "Session Tips",
              description: "Try asking about latency, sync issues, or recording options."
            });
          }}
        >
          <Sparkles className="h-3 w-3 mr-1" />
          Tips
        </Button>
      </div>
      
      <ScrollArea className="flex-1 p-3" ref={scrollAreaRef}>
        <div className="space-y-3">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[85%] p-2 rounded-lg ${
                  message.sender === 'user' 
                    ? 'bg-purple-600/50 text-white' 
                    : 'bg-white/10 text-white/90'
                }`}
              >
                <p className="text-sm">{message.content}</p>
                <div className="flex items-center mt-1 gap-1">
                  <Clock className="h-3 w-3 text-white/50" />
                  <span className="text-xs text-white/50">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>
          ))}
          
          {isProcessing && (
            <div className="flex justify-start">
              <div className="max-w-[85%] p-2 rounded-lg bg-white/10">
                <div className="flex space-x-1 items-center">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
      
      <div className="p-3 border-t border-white/10">
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Ask a question about your session..."
            className="bg-white/5 border-white/10 text-white"
            ref={inputRef}
          />
          <Button
            size="icon"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isProcessing}
            className="shrink-0"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AISessionAssistant;
